/**
 * Inline PDF viewer using the browser's native PDF engine via <iframe>.
 * - Authenticated: fetch as blob -> create object URL -> pass to iframe.
 * - Full in-app reading: scroll, zoom, page nav, find — all provided by browser.
 * - Keeps the viewer clean and dependency-free.
 */
import { useEffect, useState } from "react";
import { API } from "../../App";
import { Download, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "../ui/button";

export default function PdfViewer({ resourceId, fileName, onClose, compact = false }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let revoked = false;
    let objectUrl = null;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("trp_token") || localStorage.getItem("token");
        const res = await fetch(`${API}/resources/${resourceId}/file`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!revoked) setBlobUrl(objectUrl);
      } catch (e) {
        setError(e.message || "Failed to load PDF");
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [resourceId]);

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName || "resource.pdf";
    a.click();
  };

  const handleOpenNewTab = () => {
    if (blobUrl) window.open(blobUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className={`flex flex-col bg-[#000000] ${compact ? "" : "h-full"}`}
      data-testid="pdf-viewer"
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] bg-[#000000]">
        <div className="text-[12px] text-[#8A8783] truncate pr-4">
          {fileName || "Document"}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="text-[#C9C6C1] hover:text-white"
            onClick={handleOpenNewTab}
            disabled={!blobUrl}
            data-testid="pdf-open-new-tab"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
            Open
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-[#C9C6C1] hover:text-white"
            onClick={handleDownload}
            disabled={!blobUrl}
            data-testid="pdf-download"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            Download
          </Button>
          {onClose && (
            <Button size="sm" variant="ghost" onClick={onClose} className="text-[#C9C6C1]">
              Close
            </Button>
          )}
        </div>
      </div>

      {/* Viewer */}
      <div className="relative flex-1 bg-[#000000]" style={compact ? { height: 680 } : {}}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-[#8A8783] text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Loading PDF…
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-red-400 text-sm">
            Failed to load PDF: {error}
          </div>
        )}
        {blobUrl && !error && (
          <iframe
            src={`${blobUrl}#toolbar=1&navpanes=1&view=FitH`}
            title="PDF"
            className="w-full h-full border-0"
            data-testid="pdf-iframe"
          />
        )}
      </div>
    </div>
  );
}
